import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, X } from "lucide-react";

const SECTIONS = [
  { key: "customers", label: "Clients", route: "/customers" },
  { key: "jobs", label: "Bookings", route: "/jobs" },
  { key: "packages", label: "Packages", route: "/packages" },
  { key: "team", label: "Team", route: "/team" },
];

export default function SearchBar() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!q.trim()) { setResults(null); return; }
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q.trim())}`)
        .then((r) => { setResults(r.data); setOpen(true); })
        .catch(() => setResults(null));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = (route) => {
    navigate(`${route}?q=${encodeURIComponent(q.trim())}`);
    setOpen(false);
  };

  const total = results
    ? SECTIONS.reduce((n, s) => n + (results[s.key]?.length || 0), 0)
    : 0;

  return (
    <div className="relative w-full md:w-56 xl:w-72" ref={boxRef} data-testid="global-search">
      <div className="flex items-center gap-2 border border-border/70 rounded-sm px-3 py-2 bg-background focus-within:border-brand">
        <Search className="w-4 h-4 text-muted-foreground shrink-0" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results && setOpen(true)}
          onKeyDown={(e) => { if (e.key === "Enter" && total) { const s = SECTIONS.find((x) => results[x.key]?.length); if (s) go(s.route); } }}
          placeholder="Search clients, bookings…"
          data-testid="global-search-input"
          className="bg-transparent outline-none text-sm w-full placeholder:text-muted-foreground"
        />
        {q && (
          <button onClick={() => { setQ(""); setResults(null); setOpen(false); }} data-testid="global-search-clear" className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {open && results && (
        <div className="absolute right-0 mt-2 w-96 max-h-[70vh] overflow-auto bg-popover border border-border/70 rounded-sm shadow-lg z-50" data-testid="global-search-results">
          {total === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">No matches for “{q}”.</div>
          ) : (
            SECTIONS.map((s) => {
              const items = results[s.key] || [];
              if (!items.length) return null;
              return (
                <div key={s.key} className="py-2">
                  <div className="flex items-center justify-between px-4 pb-1">
                    <span className="label-caps text-muted-foreground">{s.label}</span>
                    <button onClick={() => go(s.route)} className="text-xs text-brand hover:underline" data-testid={`search-viewall-${s.key}`}>
                      View all
                    </button>
                  </div>
                  {items.map((it) => (
                    <button
                      key={it.id}
                      onClick={() => go(s.route)}
                      data-testid={`search-result-${s.key}-${it.id}`}
                      className="w-full text-left px-4 py-2 hover:bg-accent/60 transition-colors"
                    >
                      <div className="text-sm font-medium truncate">{it.name}</div>
                      {it.subtitle && <div className="text-xs text-muted-foreground truncate">{it.subtitle}</div>}
                    </button>
                  ))}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
